// Currency utility functions and types

export type Currency = 'USD' | 'GBP' | 'EUR';

export interface CurrencyInfo {
  code: Currency;
  symbol: string;
  name: string;
  locale: string;
}

export const SUPPORTED_CURRENCIES: Record<Currency, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE',
  },
};

export const DEFAULT_CURRENCY: Currency = 'USD';

/**
 * Format an amount with the specified currency
 */
export function formatCurrency(
  amount: number | string,
  currency: Currency = DEFAULT_CURRENCY,
  options: {
    showSymbol?: boolean;
    showCode?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string {
  const {
    showSymbol = true,
    showCode = false,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) {
    return '0.00';
  }

  const currencyInfo = SUPPORTED_CURRENCIES[currency];
  
  try {
    const formatter = new Intl.NumberFormat(currencyInfo.locale, {
      style: 'currency',
      currency: currencyInfo.code,
      minimumFractionDigits,
      maximumFractionDigits,
    });

    let formatted = formatter.format(numericAmount);

    // If showCode is true, append currency code
    if (showCode && !showSymbol) {
      formatted = `${numericAmount.toLocaleString(currencyInfo.locale, {
        minimumFractionDigits,
        maximumFractionDigits,
      })} ${currencyInfo.code}`;
    } else if (showCode) {
      formatted = `${formatted} ${currencyInfo.code}`;
    }

    return formatted;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${currencyInfo.symbol}${numericAmount.toFixed(2)}`;
  }
}

/**
 * Get currency symbol for a currency code
 */
export function getCurrencySymbol(currency: Currency): string {
  return SUPPORTED_CURRENCIES[currency]?.symbol || '$';
}

/**
 * Get currency name for a currency code
 */
export function getCurrencyName(currency: Currency): string {
  return SUPPORTED_CURRENCIES[currency]?.name || 'US Dollar';
}

/**
 * Parse a currency string back to a number
 */
export function parseCurrencyAmount(currencyString: string): number {
  // Remove currency symbols, codes, and formatting
  const cleaned = currencyString
    .replace(/[£$€]/g, '') // Remove symbols
    .replace(/[A-Z]{3}/g, '') // Remove currency codes
    .replace(/[,\s]/g, '') // Remove commas and spaces
    .trim();
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Convert amount between currencies (simplified - in real app you'd use exchange rates)
 */
export function convertCurrency(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency
): number {
  // For demo purposes, using fixed conversion rates
  // In production, you'd integrate with a real exchange rate API
  const exchangeRates: Record<Currency, Record<Currency, number>> = {
    USD: { USD: 1, GBP: 0.79, EUR: 0.85 },
    GBP: { USD: 1.27, GBP: 1, EUR: 1.08 },
    EUR: { USD: 1.18, GBP: 0.93, EUR: 1 },
  };

  if (fromCurrency === toCurrency) {
    return amount;
  }

  return amount * exchangeRates[fromCurrency][toCurrency];
}